import { MOVIES } from "@/mocks/movies";
import { ACTORS } from "@/mocks/actors";
import { NEWS } from "@/mocks/news";
import type { Actor, Movie, MovieType, NewsItem } from "@/lib/types";

export const movies = MOVIES as Movie[];
export const actors = ACTORS as Actor[];
export const newsList = NEWS as NewsItem[];

export const TYPE_LABELS: Record<MovieType, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
  animation: "动画",
};

export const TYPE_DESCRIPTIONS: Record<MovieType, string> = {
  movie: "收录院线新片与经典长片，从剧情梗概到演职员名单，一次读懂一部电影。",
  tv: "热播剧集与年代佳作，逐集梳理剧情走向，追更不再错过任何一条线索。",
  variety: "美食、访谈与文化节目，记录那些没有剧本的真实瞬间。",
  animation: "手绘与国创动画，献给仍然相信想象力的观众。",
};

export function getMovieById(id: string | number | undefined): Movie | undefined {
  if (id === undefined) return undefined;
  return movies.find((item) => String(item.id) === String(id));
}

export function getActorById(id: string | number | undefined): Actor | undefined {
  if (id === undefined) return undefined;
  return actors.find((item) => String(item.id) === String(id));
}

export function getNewsById(id: string | number | undefined): NewsItem | undefined {
  if (id === undefined) return undefined;
  return newsList.find((item) => String(item.id) === String(id));
}

export function getCastOf(movie: Movie) {
  return movie.cast.map((member) => ({
    ...member,
    actor: getActorById(member.actorId),
  }));
}

export function getFilmography(actorId: number) {
  return movies
    .filter((movie) => movie.cast.some((member) => member.actorId === actorId))
    .map((movie) => ({
      movie,
      role: movie.cast.find((member) => member.actorId === actorId)?.role ?? "",
    }));
}

export function getMoviesByType(type: string) {
  if (!type || type === "all") return movies;
  return movies.filter((movie) => movie.type === (type as MovieType));
}

export function getFeatured() {
  return movies.filter((movie) => movie.featured);
}

/** 相关推荐：先按共同演员，再按类型与题材标签补充 */
export function getRelated(movie: Movie, limit = 4) {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const sharedCast = item.cast.filter((cast) =>
        movie.cast.some((own) => own.actorId === cast.actorId),
      ).length;
      const sameType = item.type === movie.type ? 1 : 0;
      const sharedGenre = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      return { item, score: sharedCast * 4 + sameType * 2 + sharedGenre };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity);

  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getLatestMovies(limit = 8) {
  return [...movies]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getPopularMovies(limit = 5) {
  return [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getLatestNews(limit = 4) {
  return [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}

export function getRelatedNews(news: NewsItem, limit = 3) {
  return newsList
    .filter((item) => item.id !== news.id)
    .sort((a, b) => {
      const scoreA = (a.relatedMovieId === news.relatedMovieId ? 2 : 0) + (a.category === news.category ? 1 : 0);
      const scoreB = (b.relatedMovieId === news.relatedMovieId ? 2 : 0) + (b.category === news.category ? 1 : 0);
      return scoreB - scoreA;
    })
    .slice(0, limit);
}

export function formatDate(value: string) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${year} 年 ${Number(month)} 月 ${Number(day)} 日`;
}

export function shortDate(value: string) {
  if (!value) return "";
  const [, month, day] = value.split("-");
  return `${Number(month)} 月 ${Number(day)} 日`;
}

export function searchMovies(keyword: string) {
  const word = keyword.trim().toLowerCase();
  if (!word) return movies;
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.enTitle,
      movie.director,
      movie.region,
      ...movie.genres,
      ...movie.tags,
      ...movie.cast.map((cast) => getActorById(cast.actorId)?.name ?? ""),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(word);
  });
}